<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-warehouse me-2"></i>{{ $t('page.stores', 'Stores & Procurement') }}</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.total_items || 0 }}</div><div class="hms-stat-lbl">Active Items</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.low_stock || 0 }}</div><div class="hms-stat-lbl">Low Stock</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ stats.stock_outs || 0 }}</div><div class="hms-stat-lbl">Stock-outs</div></div>
        <div class="hms-stat"><div class="hms-stat-num">KSh {{ formatNumber(stats.stock_value || 0) }}</div><div class="hms-stat-lbl">Stock Value</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ suppliers.length }}</div><div class="hms-stat-lbl">Suppliers</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='stock' }" @click="tab='stock'"><i class="mdi mdi-package-variant me-1"></i>Stock</button>
        <button class="hms-tab" :class="{ active: tab==='drugrecv' }" @click="tab='drugrecv'; loadStoreStock()"><i class="mdi mdi-pill me-1"></i>Receiving</button>
        <button class="hms-tab" :class="{ active: tab==='stores' }" @click="tab='stores'"><i class="mdi mdi-store me-1"></i>Stores</button>
        <button class="hms-tab" :class="{ active: tab==='cats' }" @click="tab='cats'"><i class="mdi mdi-tag-multiple me-1"></i>Categories</button>
        <button class="hms-tab" :class="{ active: tab==='pos' }" @click="tab='pos'; loadPos()"><i class="mdi mdi-clipboard-list me-1"></i>Purchase Orders</button>
        <button class="hms-tab" :class="{ active: tab==='reqs' }" @click="tab='reqs'; loadReqs()"><i class="mdi mdi-clipboard-arrow-down me-1"></i>Ward Requisitions</button>
        <button class="hms-tab" :class="{ active: tab==='suppliers' }" @click="tab='suppliers'"><i class="mdi mdi-truck-delivery me-1"></i>Suppliers</button>
        <button class="hms-tab" :class="{ active: tab==='kemsa' }" @click="tab='kemsa'; loadSupplierOrders()"><i class="mdi mdi-medical-bag me-1"></i>KEMSA / MEDS</button>
        <button class="hms-tab" :class="{ active: tab==='rollup' }" @click="tab='rollup'; loadRollup()"><i class="mdi mdi-chart-tree me-1"></i>County Roll-up</button>
      </div>

      <!-- ============ STOCK ============ -->
      <div v-if="tab==='stock'">
        <div class="d-flex justify-content-between mb-2">
          <div class="d-flex gap-2 flex-wrap">
            <input v-model="search" class="hms-input" placeholder="Search code / name" @keyup.enter="loadItems" />
            <select v-model="catFilter" class="hms-input" @change="loadItems">
              <option value="">All categories</option>
              <option v-for="c in categories" :key="c.category_id" :value="c.category_id">{{ c.name }}</option>
            </select>
            <label class="d-flex align-items-center gap-2"><input type="checkbox" v-model="lowOnly" @change="loadItems" /> Low stock only</label>
          </div>
          <button class="hms-btn hms-btn-success" @click="openItemModal()"><i class="mdi mdi-plus"></i> New Item</button>
        </div>
        <table class="hms-table">
          <thead>
            <tr>
              <th>Code</th><th>Item</th><th>Category</th><th>Unit</th>
              <th>On hand</th><th>Reorder pt</th><th>Last price</th><th>Location</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="i in items" :key="i.item_id" :class="i.current_stock <= i.reorder_point ? 'row-warn' : (i.current_stock == 0 ? 'row-action' : '')">
              <td><strong>{{ i.item_code }}</strong></td>
              <td>{{ i.item_name }}<div class="hms-muted-sm" v-if="i.notes">{{ i.notes }}</div></td>
              <td>{{ i.category_name || '—' }}</td>
              <td>{{ i.unit }}</td>
              <td>
                <strong :class="i.current_stock <= i.reorder_point ? 'st-bad' : ''">{{ i.current_stock }}</strong>
                <span v-if="i.current_stock <= i.reorder_point" class="hms-tag hms-tag-warn ms-1">LOW</span>
              </td>
              <td>{{ i.reorder_point }}</td>
              <td>KSh {{ formatNumber(i.last_unit_price) }}</td>
              <td>{{ i.storage_location || '—' }}</td>
              <td><button class="hms-btn-link" @click="openItemModal(i)"><i class="mdi mdi-pencil"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ DRUG RECEIVING (store drug stock) ============ -->
      <div v-if="tab==='drugrecv'">
        <div class="d-flex justify-content-between mb-2">
          <div class="d-flex gap-2">
            <select v-model.number="storeFilter" class="hms-input" style="max-width:240px" @change="loadStoreStock">
              <option :value="0">All stores</option>
              <option v-for="st in hospStores" :key="st.store_id" :value="st.store_id">{{ st.store_name }}</option>
            </select>
            <select v-model="typeFilter" class="hms-input" style="max-width:200px" @change="loadStoreStock">
              <option value="">All categories</option>
              <option value="pharmaceutical">Pharmaceutical</option>
              <option value="non_pharmaceutical">Non-Pharmaceutical</option>
            </select>
          </div>
          <button class="hms-btn hms-btn-success" @click="openRecvDrug"><i class="mdi mdi-plus"></i> Receive Items</button>
        </div>
        <div v-if="!hospStores.length" class="hms-info-banner mb-2"><i class="mdi mdi-information-outline me-1"></i>
          No stores registered yet — register one under the <strong>Stores</strong> tab first.
        </div>
        <table class="hms-table">
          <thead><tr><th>Store</th><th>Item</th><th>Category</th><th>Batch</th><th>Unit</th><th>Quantity</th><th>Unit cost</th><th>Expiry</th><th>Supplier</th><th></th></tr></thead>
          <tbody>
            <tr v-for="d in storeStock" :key="d.store_stock_id" :style="d.quantity <= 0 ? 'opacity:.55' : ''">
              <td>{{ d.store_name || '—' }}</td>
              <td><strong>{{ d.drug_name }}</strong></td>
              <td><span class="hms-pill" :class="d.item_type === 'non_pharmaceutical' ? 'hms-pill-warn' : 'hms-pill-info'">{{ d.item_type === 'non_pharmaceutical' ? 'Non-Pharm' : 'Pharm' }}</span></td>
              <td>{{ d.batch_number || '—' }}</td>
              <td>{{ d.unit || '—' }}</td>
              <td><strong>{{ d.quantity }}</strong></td>
              <td>KSh {{ formatNumber(d.unit_cost) }}</td>
              <td>{{ d.expiry_date || '—' }}</td>
              <td>{{ d.supplier || '—' }}</td>
              <td>
                <button class="hms-btn hms-btn-blue hms-btn-sm" :disabled="d.quantity <= 0" @click="openXfer(d)" title="Send to Pharmacy">
                  <i class="mdi mdi-arrow-right-bold-circle"></i> To Pharmacy
                </button>
              </td>
            </tr>
            <tr v-if="!storeStock.length"><td colspan="10" class="text-center hms-muted">Nothing received into stores yet. Click "Receive Items" to record a delivery.</td></tr>
          </tbody>
        </table>
      </div>

      <!-- ============ STORES REGISTRY ============ -->
      <div v-if="tab==='stores'">
        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-success" @click="openStoreModal()"><i class="mdi mdi-plus"></i> Register Store</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>Store</th><th>Location</th><th>Description</th><th>Status</th><th></th></tr></thead>
          <tbody>
            <tr v-for="st in hospStores" :key="st.store_id" :style="st.is_active != 1 ? 'opacity:.55' : ''">
              <td><strong>{{ st.store_name }}</strong></td>
              <td>{{ st.location || '—' }}</td>
              <td>{{ st.description || '—' }}</td>
              <td><span class="hms-pill" :class="st.is_active == 1 ? 'hms-pill-success' : 'hms-pill-muted'">{{ st.is_active == 1 ? 'Active' : 'Inactive' }}</span></td>
              <td class="text-nowrap">
                <button class="hms-btn-link" @click="openStoreModal(st)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                <button class="hms-btn-link" @click="toggleStore(st)" :title="st.is_active == 1 ? 'Deactivate' : 'Reactivate'">
                  <i class="mdi" :class="st.is_active == 1 ? 'mdi-store-off' : 'mdi-store-check'"></i>
                </button>
              </td>
            </tr>
            <tr v-if="!hospStores.length"><td colspan="5" class="text-center hms-muted">No stores registered yet — click "Register Store" to add e.g. Main Store, Pharmacy Store.</td></tr>
          </tbody>
        </table>
      </div>

      <!-- ============ CATEGORIES ============ -->
      <div v-if="tab==='cats'">
        <div class="d-flex justify-content-between mb-2">
          <span class="hms-muted-sm">Shared defaults are read-only. Your own categories can be edited or deleted (when no active items use them).</span>
          <button class="hms-btn hms-btn-success" @click="openCatModal()"><i class="mdi mdi-plus"></i> Add Category</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>Name</th><th>Description</th><th>Scope</th><th></th></tr></thead>
          <tbody>
            <tr v-for="c in categories" :key="c.category_id">
              <td><strong>{{ c.name }}</strong></td>
              <td>{{ c.description || '—' }}</td>
              <td>
                <span v-if="c.is_shared == 1" class="hms-pill hms-pill-muted">Shared default</span>
                <span v-else class="hms-pill hms-pill-info">Your hospital</span>
              </td>
              <td class="text-nowrap">
                <button v-if="c.is_shared != 1" class="hms-btn-link" @click="openCatModal(c)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                <button v-if="c.is_shared != 1" class="hms-btn-link text-danger" @click="deleteCat(c)" title="Delete"><i class="mdi mdi-trash-can"></i></button>
              </td>
            </tr>
            <tr v-if="!categories.length"><td colspan="4" class="text-center hms-muted">No categories yet.</td></tr>
          </tbody>
        </table>
      </div>

      <!-- ============ POs ============ -->
      <div v-if="tab==='pos'">
        <div class="d-flex justify-content-between mb-2">
          <select v-model="poStatusFilter" class="hms-input" @change="loadPos" style="max-width:200px">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="approved">Approved</option>
            <option value="sent">Sent</option>
            <option value="partially_received">Partially received</option>
            <option value="received">Received</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button class="hms-btn hms-btn-success" @click="openPoModal()"><i class="mdi mdi-plus"></i> New PO</button>
        </div>
        <table class="hms-table">
          <thead>
            <tr><th>PO #</th><th>Supplier</th><th>Order date</th><th>Expected</th><th>Lines</th><th>Total</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="p in pos" :key="p.po_id">
              <td><strong>{{ p.po_number }}</strong></td>
              <td>{{ p.supplier_name }}</td>
              <td>{{ p.order_date }}</td>
              <td>{{ p.expected_delivery || '—' }}</td>
              <td>{{ p.line_count }}</td>
              <td>KSh {{ formatNumber(p.total_amount) }}</td>
              <td><span class="hms-pill" :class="poStatusPill(p.status)">{{ formatPoStatus(p.status) }}</span></td>
              <td>
                <button v-if="p.status === 'draft'" class="hms-btn hms-btn-blue hms-btn-sm" @click="approvePo(p)"><i class="mdi mdi-check"></i> Approve</button>
                <button v-if="['approved','sent','partially_received'].includes(p.status)" class="hms-btn hms-btn-success hms-btn-sm" @click="openReceiveModal(p)"><i class="mdi mdi-truck-delivery"></i> Receive</button>
                <button class="hms-btn-link" @click="openPoDetail(p)"><i class="mdi mdi-eye"></i></button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ REQUISITIONS ============ -->
      <div v-if="tab==='reqs'">
        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-success" @click="openReqModal()"><i class="mdi mdi-plus"></i> New Requisition</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>REQ #</th><th>Department</th><th>Date</th><th>Lines</th><th>Status</th><th></th></tr></thead>
          <tbody>
            <tr v-for="r in reqs" :key="r.req_id">
              <td><strong>{{ r.req_number }}</strong></td>
              <td>{{ r.requesting_dept }}</td>
              <td>{{ r.request_date }}</td>
              <td>{{ r.line_count }}</td>
              <td><span class="hms-pill" :class="reqStatusPill(r.status)">{{ formatReqStatus(r.status) }}</span></td>
              <td>
                <button v-if="['submitted','approved','partially_issued'].includes(r.status)" class="hms-btn hms-btn-success hms-btn-sm" @click="openIssueModal(r)"><i class="mdi mdi-arrow-up-bold-circle"></i> Issue</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ SUPPLIERS ============ -->
      <div v-if="tab==='suppliers'">
        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-success" @click="openSupplierModal()"><i class="mdi mdi-plus"></i> Add Supplier</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>Code</th><th>Name</th><th>Contact</th><th>Phone</th><th>Category</th><th>Prequalified</th><th>Status</th><th></th></tr></thead>
          <tbody>
            <tr v-for="s in suppliers" :key="s.supplier_id" :style="s.is_active != 1 ? 'opacity:.55' : ''">
              <td><strong>{{ s.code || '—' }}</strong></td>
              <td>{{ s.name }}<div class="hms-muted-sm" v-if="s.kra_pin">KRA: {{ s.kra_pin }}</div></td>
              <td>{{ s.contact_person || '—' }}</td>
              <td>{{ s.phone || '—' }}</td>
              <td>{{ s.category || '—' }}</td>
              <td><span v-if="s.prequalified == 1" class="hms-pill hms-pill-success">Prequalified</span><span v-else class="hms-pill hms-pill-muted">No</span></td>
              <td><span class="hms-pill" :class="s.is_active == 1 ? 'hms-pill-success' : 'hms-pill-muted'">{{ s.is_active == 1 ? 'Active' : 'Inactive' }}</span></td>
              <td class="text-nowrap">
                <button class="hms-btn-link" @click="openSupplierModal(s)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                <button class="hms-btn-link" @click="toggleSupplier(s)" :title="s.is_active == 1 ? 'Deactivate' : 'Reactivate'">
                  <i class="mdi" :class="s.is_active == 1 ? 'mdi-account-off' : 'mdi-account-check'"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ KEMSA / MEDS ============ -->
      <div v-if="tab==='kemsa'">
        <div class="d-flex justify-content-between mb-2">
          <h5 class="m-0">KEMSA &amp; MEDS Orders</h5>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-primary" @click="openSupplierOrder('KEMSA')"><i class="mdi mdi-plus me-1"></i>New KEMSA order</button>
            <button class="hms-btn hms-btn-blue" @click="openSupplierOrder('MEDS')"><i class="mdi mdi-plus me-1"></i>New MEDS order</button>
          </div>
        </div>
        <div class="hms-info-banner mb-2"><i class="mdi mdi-information-outline me-1"></i>
          Currently submits to the in-codebase mock receiver. Flip to live KEMSA/MEDS by setting <code>CRAFTCARE_KEMSA_URL</code> / <code>CRAFTCARE_MEDS_URL</code>.
        </div>
        <table class="hms-table">
          <thead><tr><th>Order #</th><th>Supplier</th><th>Status</th><th>External ref</th><th>Total (KSh)</th><th>Submitted</th></tr></thead>
          <tbody>
            <tr v-for="o in supplierOrders" :key="o.order_id">
              <td>#{{ o.order_id }}</td>
              <td><span class="hms-badge" :class="o.supplier === 'KEMSA' ? 'hms-badge-success' : 'hms-badge-info'">{{ o.supplier }}</span></td>
              <td>{{ o.status }}</td>
              <td><code>{{ o.external_ref || '—' }}</code></td>
              <td class="text-end">{{ (o.order_total || 0).toLocaleString() }}</td>
              <td>{{ o.submitted_at || '—' }}</td>
            </tr>
            <tr v-if="!supplierOrders.length"><td colspan="6" class="text-center hms-muted">No supplier orders yet.</td></tr>
          </tbody>
        </table>
      </div>

      <!-- ============ COUNTY ROLL-UP ============ -->
      <div v-if="tab==='rollup'">
        <div class="d-flex justify-content-between mb-2">
          <h5 class="m-0">County Stock Roll-up</h5>
          <button class="hms-btn hms-btn-blue" @click="loadRollup"><i class="mdi mdi-refresh"></i></button>
        </div>
        <table class="hms-table">
          <thead><tr><th>Facility</th><th class="text-end">SKUs</th><th class="text-end">Stock value (KSh)</th><th class="text-end">Items at low stock</th></tr></thead>
          <tbody>
            <tr v-for="r in rollup" :key="r.hospital_id">
              <td>{{ r.hospital_name }}</td>
              <td class="text-end">{{ r.sku_count }}</td>
              <td class="text-end">{{ (r.stock_value || 0).toLocaleString() }}</td>
              <td class="text-end"><span v-if="r.items_at_low_stock > 0" class="hms-badge hms-badge-amber">{{ r.items_at_low_stock }}</span><span v-else>0</span></td>
            </tr>
            <tr v-if="!rollup.length"><td colspan="4" class="text-center hms-muted">No facilities to roll up. (County-level scope required.)</td></tr>
          </tbody>
        </table>
      </div>

      <!-- ====== KEMSA ORDER MODAL ====== -->
      <div v-if="supplierOrderModal" class="hms-modal-bg" @click.self="supplierOrderModal=false">
        <div class="hms-modal" style="width:720px;">
          <div class="hms-modal-head"><h5>New {{ supplierOrderForm.supplier }} order</h5><button class="hms-icon-btn" @click="supplierOrderModal=false"><i class="mdi mdi-close"></i></button></div>
          <div class="hms-modal-body">
            <p class="hms-muted-sm">Add line items, then submit. The system will queue and POST to the supplier endpoint.</p>
            <table class="hms-table">
              <thead><tr><th>Code</th><th>Name</th><th>Qty</th><th>Unit cost</th><th></th></tr></thead>
              <tbody>
                <tr v-for="(it, idx) in supplierOrderForm.items" :key="idx">
                  <td><input v-model="it.product_code" class="hms-input hms-input-sm" /></td>
                  <td><input v-model="it.product_name" class="hms-input hms-input-sm" /></td>
                  <td><input v-model.number="it.qty" type="number" class="hms-input hms-input-sm" style="width:100px;" /></td>
                  <td><input v-model.number="it.unit_cost" type="number" class="hms-input hms-input-sm" style="width:120px;" /></td>
                  <td><button class="hms-btn-icon" @click="supplierOrderForm.items.splice(idx,1)"><i class="mdi mdi-close"></i></button></td>
                </tr>
              </tbody>
            </table>
            <button class="hms-btn hms-btn-blue hms-btn-sm" @click="supplierOrderForm.items.push({product_code:'',product_name:'',qty:1,unit_cost:0})"><i class="mdi mdi-plus"></i> Add line</button>
          </div>
          <div class="hms-modal-foot">
            <button class="hms-btn hms-btn-neutral" @click="supplierOrderModal=false">Cancel</button>
            <button class="hms-btn hms-btn-primary" @click="submitSupplierOrder" :disabled="!supplierOrderForm.items.length"><i class="mdi mdi-send me-1"></i>Submit to {{ supplierOrderForm.supplier }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ====== ITEM MODAL ====== -->
    <div v-if="itemModal" class="hms-modal-bg" @click.self="itemModal=false">
      <div class="hms-modal" style="width:600px">
        <div class="hms-modal-head"><h5>{{ item.item_id ? 'Edit Item' : 'New Item' }}</h5><button class="hms-icon-btn" @click="itemModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-4"><label class="hms-label">Code *</label><input v-model="item.item_code" class="hms-input" /></div>
            <div class="col-8"><label class="hms-label">Name *</label><input v-model="item.item_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Category</label>
              <select v-model.number="item.category_id" class="hms-input">
                <option :value="null">—</option>
                <option v-for="c in categories" :key="c.category_id" :value="c.category_id">{{ c.name }}</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Unit</label>
              <select v-model="item.unit" class="hms-input">
                <option value="pc">pc</option><option value="box">box</option>
                <option value="pack">pack</option><option value="L">litre</option>
                <option value="kg">kg</option><option value="ml">ml</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Pack size</label><input v-model.number="item.pack_size" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Reorder point</label><input v-model.number="item.reorder_point" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Reorder qty</label><input v-model.number="item.reorder_quantity" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Last unit price</label><input v-model.number="item.last_unit_price" type="number" step="0.01" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Preferred supplier</label>
              <select v-model.number="item.preferred_supplier_id" class="hms-input">
                <option :value="null">—</option>
                <option v-for="s in activeSuppliers" :key="s.supplier_id" :value="s.supplier_id">{{ s.name }}</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Storage location</label><input v-model="item.storage_location" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="item.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="itemModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveItem">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- ====== SUPPLIER MODAL ====== -->
    <div v-if="supplierModal" class="hms-modal-bg" @click.self="supplierModal=false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head"><h5>{{ supplier.supplier_id ? 'Edit Supplier' : 'Add Supplier' }}</h5><button class="hms-icon-btn" @click="supplierModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-4"><label class="hms-label">Code</label><input v-model="supplier.code" class="hms-input" /></div>
            <div class="col-8"><label class="hms-label">Name *</label><input v-model="supplier.name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">KRA PIN</label><input v-model="supplier.kra_pin" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Business reg #</label><input v-model="supplier.business_reg_no" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Contact person</label><input v-model="supplier.contact_person" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Phone</label><input v-model="supplier.phone" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Email</label><input v-model="supplier.email" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Category</label><input v-model="supplier.category" class="hms-input" placeholder="PPE / Lab / Stationery…" /></div>
            <div class="col-12"><label class="hms-label">Address</label><input v-model="supplier.address" class="hms-input" /></div>
            <div class="col-4 d-flex align-items-end"><label><input type="checkbox" v-model="supplier.prequalified" :true-value="1" :false-value="0" /> Prequalified</label></div>
            <div class="col-4"><label class="hms-label">PQ expiry</label><input v-model="supplier.prequalification_expiry" type="date" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="supplier.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="supplierModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveSupplier">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- ====== PO MODAL ====== -->
    <div v-if="poModal" class="hms-modal-bg" @click.self="poModal=false">
      <div class="hms-modal" style="width:780px">
        <div class="hms-modal-head"><h5>New Purchase Order</h5><button class="hms-icon-btn" @click="poModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2 mb-2">
            <div class="col-6"><label class="hms-label">Supplier *</label>
              <select v-model.number="po.supplier_id" class="hms-input">
                <option :value="null">—</option>
                <option v-for="s in activeSuppliers" :key="s.supplier_id" :value="s.supplier_id">{{ s.name }}</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Order date</label><input v-model="po.order_date" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Expected delivery</label><input v-model="po.expected_delivery" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Funding source</label>
              <select v-model="po.revenue_source" class="hms-input">
                <option value="FIF">FIF (retained)</option>
                <option value="FIN">County Finance Act</option>
                <option value="GOK">GoK conditional grant</option>
                <option value="DONOR">Donor / partner</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Notes</label><input v-model="po.notes" class="hms-input" /></div>
          </div>
          <div class="hms-section-label d-flex justify-content-between">
            <span>LINE ITEMS</span>
            <button class="hms-btn-link" @click="addPoLine">+ Add line</button>
          </div>
          <table class="hms-table">
            <thead><tr><th>Item</th><th>Qty</th><th>Unit price</th><th>Total</th><th></th></tr></thead>
            <tbody>
              <tr v-for="(ln, idx) in po.lines" :key="idx">
                <td>
                  <select v-model.number="ln.item_id" class="hms-input">
                    <option :value="null">— select —</option>
                    <option v-for="i in items" :key="i.item_id" :value="i.item_id">{{ i.item_code }} — {{ i.item_name }}</option>
                  </select>
                </td>
                <td><input v-model.number="ln.quantity" type="number" min="0" step="0.01" class="hms-input" style="width:90px" /></td>
                <td><input v-model.number="ln.unit_price" type="number" min="0" step="0.01" class="hms-input" style="width:120px" /></td>
                <td>KSh {{ formatNumber((ln.quantity || 0) * (ln.unit_price || 0)) }}</td>
                <td><button class="hms-btn-link text-danger" @click="po.lines.splice(idx, 1)"><i class="mdi mdi-trash-can"></i></button></td>
              </tr>
            </tbody>
            <tfoot>
              <tr><td colspan="3" class="text-end"><strong>Total</strong></td><td><strong>KSh {{ formatNumber(poTotal) }}</strong></td><td></td></tr>
            </tfoot>
          </table>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="poModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="savePo">{{ saving?'Saving…':'Save PO' }}</button>
        </div>
      </div>
    </div>

    <!-- ====== RECEIVE PO MODAL ====== -->
    <div v-if="recvModal" class="hms-modal-bg" @click.self="recvModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>Receive Goods — {{ recv.po_number }}</h5><button class="hms-icon-btn" @click="recvModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-form-group">
            <label class="hms-label">Delivery note #</label>
            <input v-model="recv.delivery_note" class="hms-input" />
          </div>
          <table class="hms-table">
            <thead><tr><th>Item</th><th>Ordered</th><th>Already received</th><th>Receiving now</th></tr></thead>
            <tbody>
              <tr v-for="ln in recv.lines" :key="ln.po_item_id">
                <td>{{ ln.item_code }} — {{ ln.item_name }}</td>
                <td>{{ ln.quantity }}</td>
                <td>{{ ln.received_qty }}</td>
                <td><input v-model.number="ln.now_receiving" type="number" min="0" :max="ln.quantity - ln.received_qty" step="0.01" class="hms-input" style="width:120px" /></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="recvModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveReceive">{{ saving?'Saving…':'Confirm Receipt' }}</button>
        </div>
      </div>
    </div>

    <!-- ====== ISSUE REQ MODAL ====== -->
    <div v-if="issueModal" class="hms-modal-bg" @click.self="issueModal=false">
      <div class="hms-modal" style="width:780px">
        <div class="hms-modal-head">
          <h5>Issue from Stores — {{ issue.req_number }} ({{ issue.requesting_dept }})</h5>
          <button class="hms-icon-btn" @click="issueModal=false"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="hms-modal-body">
          <table class="hms-table">
            <thead><tr><th>Item</th><th>Requested</th><th>Already issued</th><th>In stock</th><th>Issuing now</th></tr></thead>
            <tbody>
              <tr v-for="ln in issue.lines" :key="ln.req_item_id" :class="ln.current_stock < (ln.quantity_requested - ln.quantity_issued) ? 'row-warn' : ''">
                <td>{{ ln.item_code }} — {{ ln.item_name }} <span class="hms-muted-sm">({{ ln.unit }})</span></td>
                <td>{{ ln.quantity_requested }}</td>
                <td>{{ ln.quantity_issued }}</td>
                <td :class="ln.current_stock <= 0 ? 'st-bad' : ''">{{ ln.current_stock }}</td>
                <td><input v-model.number="ln.now_issuing" type="number" min="0" :max="Math.min(ln.quantity_requested - ln.quantity_issued, ln.current_stock)" step="0.01" class="hms-input" style="width:120px" /></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="issueModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveIssue">{{ saving?'Issuing…':'Confirm Issue' }}</button>
        </div>
      </div>
    </div>

    <!-- ====== REQ MODAL ====== -->
    <div v-if="reqModal" class="hms-modal-bg" @click.self="reqModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>New Ward Requisition</h5><button class="hms-icon-btn" @click="reqModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-form-group">
            <label class="hms-label">Requesting department *</label>
            <input v-model="req.requesting_dept" class="hms-input" placeholder="Maternity / OPD / Theatre / Lab…" />
          </div>
          <div class="hms-section-label d-flex justify-content-between">
            <span>ITEMS</span>
            <button class="hms-btn-link" @click="addReqLine">+ Add line</button>
          </div>
          <table class="hms-table">
            <thead><tr><th>Item</th><th>Quantity</th><th></th></tr></thead>
            <tbody>
              <tr v-for="(ln, idx) in req.lines" :key="idx">
                <td>
                  <select v-model.number="ln.item_id" class="hms-input">
                    <option :value="null">— select —</option>
                    <option v-for="i in items" :key="i.item_id" :value="i.item_id">{{ i.item_code }} — {{ i.item_name }} (in stock: {{ i.current_stock }})</option>
                  </select>
                </td>
                <td><input v-model.number="ln.quantity" type="number" min="0" class="hms-input" style="width:120px" /></td>
                <td><button class="hms-btn-link text-danger" @click="req.lines.splice(idx, 1)"><i class="mdi mdi-trash-can"></i></button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="reqModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveReq">{{ saving?'Saving…':'Submit Requisition' }}</button>
        </div>
      </div>
    </div>

    <!-- ====== CATEGORY MODAL ====== -->
    <div v-if="catModal" class="hms-modal-bg" @click.self="catModal=false">
      <div class="hms-modal" style="width:480px">
        <div class="hms-modal-head"><h5>{{ catForm.category_id ? 'Edit Category' : 'Add Category' }}</h5><button class="hms-icon-btn" @click="catModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-12"><label class="hms-label">Name *</label><input v-model="catForm.name" class="hms-input" placeholder="e.g. Vaccines, Lab consumables, Cleaning…" /></div>
            <div class="col-12"><label class="hms-label">Description</label><textarea v-model="catForm.description" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="catModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveCat">{{ saving?'Saving…':'Save Category' }}</button>
        </div>
      </div>
    </div>

    <!-- ====== STORE MODAL ====== -->
    <div v-if="storeModal" class="hms-modal-bg" @click.self="storeModal=false">
      <div class="hms-modal" style="width:480px">
        <div class="hms-modal-head"><h5>{{ storeForm.store_id ? 'Edit Store' : 'Register Store' }}</h5><button class="hms-icon-btn" @click="storeModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-12"><label class="hms-label">Store name *</label><input v-model="storeForm.store_name" class="hms-input" placeholder="Main Store / Pharmacy Store / Cold Room…" /></div>
            <div class="col-12"><label class="hms-label">Location</label><input v-model="storeForm.location" class="hms-input" placeholder="Block B, Ground floor…" /></div>
            <div class="col-12"><label class="hms-label">Description</label><textarea v-model="storeForm.description" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="storeModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveStore">{{ saving?'Saving…':'Save Store' }}</button>
        </div>
      </div>
    </div>

    <!-- ====== RECEIVE DRUGS MODAL ====== -->
    <div v-if="recvDrugModal" class="hms-modal-bg" @click.self="recvDrugModal=false">
      <div class="hms-modal" style="width:620px">
        <div class="hms-modal-head"><h5>Receive Items into Store</h5><button class="hms-icon-btn" @click="recvDrugModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Receiving store *</label>
              <select v-model.number="recvDrug.store_id" class="hms-input">
                <option :value="0">— select store —</option>
                <option v-for="st in activeStores" :key="st.store_id" :value="st.store_id">{{ st.store_name }}</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Supplier</label>
              <select v-model="recvDrug.supplier" class="hms-input">
                <option value="">Select supplier…</option>
                <option v-for="s in activeSuppliers" :key="s.supplier_id" :value="s.name">{{ s.name }}</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Category *</label>
              <select v-model="recvDrug.item_type" class="hms-input">
                <option value="pharmaceutical">Pharmaceutical (drugs)</option>
                <option value="non_pharmaceutical">Non-Pharmaceutical (gloves, syringes, reagents…)</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">{{ recvDrug.item_type === 'non_pharmaceutical' ? 'Item name *' : 'Drug name *' }}</label>
              <select v-if="!recvDrug.drug_name_other" v-model="recvDrug.drug_name" class="hms-input" @change="onRecvDrugPick">
                <option value="">— select {{ recvDrug.item_type === 'non_pharmaceutical' ? 'item' : 'drug' }} —</option>
                <option v-for="n in knownItemNames" :key="n" :value="n">{{ n }}</option>
                <option value="__other__">+ Other (type new)…</option>
              </select>
              <input v-else v-model="recvDrug.drug_name" class="hms-input"
                     :placeholder="recvDrug.item_type === 'non_pharmaceutical' ? 'e.g. Latex gloves, medium' : 'e.g. Amoxicillin 500mg caps'" />
              <button v-if="recvDrug.drug_name_other" type="button" class="hms-btn-link" @click="recvDrug.drug_name_other=false; recvDrug.drug_name=''" style="padding:0; margin-top:2px; font-size:11px;">
                ← Pick from list instead
              </button>
            </div>
            <div class="col-4"><label class="hms-label">Unit</label><input v-model="recvDrug.unit" class="hms-input" :placeholder="recvDrug.item_type === 'non_pharmaceutical' ? 'boxes / pieces / packs' : 'tablets / vials / bottles'" /></div>
            <div class="col-4"><label class="hms-label">Batch number</label><input v-model="recvDrug.batch_number" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Quantity received *</label><input v-model.number="recvDrug.quantity" type="number" min="0" step="0.01" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Unit cost (KSh)</label><input v-model.number="recvDrug.unit_cost" type="number" min="0" step="0.01" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Expiry date</label><input v-model="recvDrug.expiry_date" type="date" class="hms-input" /></div>
          </div>
          <div v-if="recvDrugError" class="hms-form-error mt-2">{{ recvDrugError }}</div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="recvDrugModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveRecvDrug">{{ saving?'Saving…':'Receive into Store' }}</button>
        </div>
      </div>
    </div>

    <!-- ====== TRANSFER TO PHARMACY MODAL ====== -->
    <div v-if="xferModal" class="hms-modal-bg" @click.self="xferModal=false">
      <div class="hms-modal" style="width:460px">
        <div class="hms-modal-head"><h5>Send to Pharmacy</h5><button class="hms-icon-btn" @click="xferModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <p class="mb-2"><strong>{{ xfer.drug_name }}</strong> <span class="hms-muted-sm" v-if="xfer.batch_number">batch {{ xfer.batch_number }}</span><br />
            <span class="hms-muted-sm">From {{ xfer.store_name }} — {{ xfer.available }} available</span></p>
          <label class="hms-label">Quantity to transfer *</label>
          <input v-model.number="xfer.quantity" type="number" min="0" :max="xfer.available" step="0.01" class="hms-input" />
          <div v-if="xferError" class="hms-form-error mt-2">{{ xferError }}</div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="xferModal=false">Cancel</button>
          <button class="hms-btn hms-btn-blue" :disabled="saving" @click="saveXfer">{{ saving?'Transferring…':'Transfer to Pharmacy' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "StoresManagement",
  components: { Navigation },
  data() {
    return {
      tab: 'stock', saving: false,
      stats: {},
      items: [], categories: [], suppliers: [],
      pos: [], reqs: [],

      search: '', catFilter: '', lowOnly: false,
      poStatusFilter: '',

      hospStores: [], storeStock: [], storeFilter: 0, typeFilter: '',
      pharmacyStock: [],
      catModal: false, catForm: { category_id: null, name: '', description: '' },
      storeModal: false, storeForm: { store_id: null, store_name: '', location: '', description: '' },
      recvDrugModal: false, recvDrugError: '',
      recvDrug: { store_id: 0, supplier: '', item_type: 'pharmaceutical', drug_name: '', drug_name_other: false, unit: '', batch_number: '', quantity: null, unit_cost: null, expiry_date: '' },
      xferModal: false, xferError: '',
      xfer: { store_stock_id: null, drug_name: '', batch_number: '', store_name: '', available: 0, quantity: null },

      itemModal: false, item: this.blankItem(),
      supplierModal: false, supplier: this.blankSupplier(),
      poModal: false, po: this.blankPo(),
      recvModal: false, recv: { po_id: null, po_number: '', delivery_note: '', lines: [] },
      reqModal: false, req: { requesting_dept: '', lines: [{ item_id: null, quantity: null }] },
      issueModal: false, issue: { req_id: null, req_number: '', requesting_dept: '', lines: [] },

      supplierOrders: [],
      supplierOrderModal: false,
      supplierOrderForm: { supplier: 'KEMSA', items: [] },
      rollup: []
    };
  },
  computed: {
    poTotal() {
      return (this.po.lines || []).reduce((s, l) => s + (parseFloat(l.quantity || 0) * parseFloat(l.unit_price || 0)), 0);
    },
    activeSuppliers() { return this.suppliers.filter(s => s.is_active == 1); },
    activeStores() { return this.hospStores.filter(s => s.is_active == 1); },
    knownItemNames() {
      const seen = new Set();
      const isNonPharm = this.recvDrug.item_type === 'non_pharmaceutical';
      (this.storeStock || []).forEach(d => {
        if (!d.drug_name) return;
        const rowIsNonPharm = d.item_type === 'non_pharmaceutical';
        if (rowIsNonPharm === isNonPharm) seen.add(d.drug_name);
      });
      if (!isNonPharm) {
        (this.pharmacyStock || []).forEach(s => { if (s.drug_name) seen.add(s.drug_name); });
      }
      return Array.from(seen).sort((a, b) => a.localeCompare(b));
    }
  },
  mounted() { this.loadAll(); },
  methods: {
    blankItem() { return { item_id: null, item_code: '', item_name: '', category_id: null, unit: 'pc', pack_size: 1, reorder_point: 0, reorder_quantity: 0, last_unit_price: 0, storage_location: '', preferred_supplier_id: null, notes: '' }; },
    async loadSupplierOrders() {
      try {
        const r = await executeJson({ function: 'listSupplierOrders' }, hms);
        if (r.data && r.data.success) this.supplierOrders = (r.data.data && r.data.data.orders) || [];
      } catch(e) { /* swallow */ }
    },
    openSupplierOrder(sup) {
      this.supplierOrderForm = { supplier: sup, items: [{ product_code: '', product_name: '', qty: 1, unit_cost: 0 }] };
      this.supplierOrderModal = true;
    },
    async submitSupplierOrder() {
      const fn = this.supplierOrderForm.supplier === 'KEMSA' ? 'kemsaSubmitOrder' : 'medsSubmitOrder';
      const items = this.supplierOrderForm.items.filter(it => it.product_code && it.qty > 0);
      if (!items.length) return;
      try {
        const r = await executeJson({ function: fn, items_json: JSON.stringify(items) }, hms);
        if (r.data && r.data.success) { this.supplierOrderModal = false; await this.loadSupplierOrders(); }
      } catch(e) { /* swallow */ }
    },
    async loadRollup() {
      try {
        const r = await executeJson({ function: 'getCountyStockRollup' }, hms);
        if (r.data && r.data.success) this.rollup = (r.data.data && r.data.data.rollup) || [];
      } catch(e) { /* swallow */ }
    },
    blankSupplier() { return { supplier_id: null, code: '', name: '', kra_pin: '', business_reg_no: '', contact_person: '', phone: '', email: '', address: '', category: '', prequalified: 0, prequalification_expiry: '', notes: '' }; },
    blankPo() { return { supplier_id: null, order_date: new Date().toISOString().substring(0,10), expected_delivery: '', revenue_source: 'FIF', notes: '', lines: [{ item_id: null, quantity: null, unit_price: null }] }; },

    async loadAll() {
      try {
        const [it, cat, sup, sto] = await Promise.all([
          executeJson({ function: 'getStoresItems', search: this.search, category_id: this.catFilter, low_stock: this.lowOnly ? 1 : 0 }, hms),
          executeJson({ function: 'getStoresCategories' }, hms),
          executeJson({ function: 'getStoresSuppliers', include_inactive: 1 }, hms),
          executeJson({ function: 'getHospitalStores', include_inactive: 1 }, hms)
        ]);
        if (it.data && it.data.success)  { const d = it.data.data || {};  this.items = d.Items || []; this.stats = d.Stats || {}; }
        if (cat.data && cat.data.success) { this.categories = (cat.data.data && cat.data.data.Categories) || []; }
        if (sup.data && sup.data.success) { this.suppliers  = (sup.data.data && sup.data.data.Suppliers)  || []; }
        if (sto.data && sto.data.success) { this.hospStores = (sto.data.data && sto.data.data.Stores)     || []; }
      } catch (e) { console.error(e); }
    },
    async loadItems() {
      try {
        const r = await executeJson({ function: 'getStoresItems', search: this.search, category_id: this.catFilter, low_stock: this.lowOnly ? 1 : 0 }, hms);
        if (r.data && r.data.success) { const d = r.data.data || {}; this.items = d.Items || []; this.stats = d.Stats || {}; }
      } catch (e) { console.error(e); }
    },

    async loadCategories() {
      try {
        const r = await executeJson({ function: 'getStoresCategories' }, hms);
        if (r.data && r.data.success) this.categories = (r.data.data && r.data.data.Categories) || [];
      } catch (e) { console.error(e); }
    },
    openCatModal(c) {
      this.catForm = c
        ? { category_id: c.category_id, name: c.name || '', description: c.description || '' }
        : { category_id: null, name: '', description: '' };
      this.catModal = true;
    },
    async saveCat() {
      if (!this.catForm.name || !this.catForm.name.trim()) { alert('Category name is required.'); return; }
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveStoresCategory', ...this.catForm }, hms);
        if (r.data && r.data.success) { this.catModal = false; await this.loadCategories(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async deleteCat(c) {
      if (!confirm('Delete category "' + c.name + '"? Items already tagged with it will keep the tag until re-assigned.')) return;
      try {
        const r = await executeJson({ function: 'deleteStoresCategory', category_id: c.category_id }, hms);
        if (r.data && r.data.success) await this.loadCategories();
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
    },
    async loadStores() {
      try {
        const r = await executeJson({ function: 'getHospitalStores', include_inactive: 1 }, hms);
        if (r.data && r.data.success) this.hospStores = (r.data.data && r.data.data.Stores) || [];
      } catch (e) { console.error(e); }
    },
    async loadStoreStock() {
      try {
        const r = await executeJson({ function: 'getStoreDrugStock', store_id: this.storeFilter || 0, item_type: this.typeFilter }, hms);
        if (r.data && r.data.success) this.storeStock = (r.data.data && r.data.data.StoreStock) || [];
      } catch (e) { console.error(e); }
    },
    openStoreModal(st) {
      this.storeForm = st
        ? { store_id: st.store_id, store_name: st.store_name, location: st.location || '', description: st.description || '' }
        : { store_id: null, store_name: '', location: '', description: '' };
      this.storeModal = true;
    },
    async saveStore() {
      if (!this.storeForm.store_name || !this.storeForm.store_name.trim()) { alert('Store name is required.'); return; }
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveHospitalStore', ...this.storeForm }, hms);
        if (r.data && r.data.success) { this.storeModal = false; await this.loadStores(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async toggleStore(st) {
      const activate = st.is_active != 1;
      if (!activate && !confirm('Deactivate store "' + st.store_name + '"? It will no longer accept drug receipts.')) return;
      try {
        const r = await executeJson({ function: 'toggleHospitalStore', store_id: st.store_id, is_active: activate ? 1 : 0 }, hms);
        if (r.data && r.data.success) await this.loadStores();
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
    },
    onRecvDrugPick() {
      if (this.recvDrug.drug_name === '__other__') {
        this.recvDrug.drug_name = '';
        this.recvDrug.drug_name_other = true;
      }
    },
    async openRecvDrug() {
      this.recvDrugError = '';
      this.recvDrug = { store_id: this.storeFilter || 0, supplier: '', item_type: this.typeFilter || 'pharmaceutical', drug_name: '', drug_name_other: false, unit: '', batch_number: '', quantity: null, unit_cost: null, expiry_date: '' };
      this.recvDrugModal = true;
      try {
        const [ss, ph] = await Promise.all([
          executeJson({ function: 'getStoreDrugStock', store_id: 0 }, hms),
          executeJson({ function: 'getPharmacyStock' }, hms)
        ]);
        if (ss.data && ss.data.success) this.storeStock = (ss.data.data && ss.data.data.StoreStock) || this.storeStock;
        if (ph.data && ph.data.success) this.pharmacyStock = (ph.data.data && ph.data.data.Stock) || [];
      } catch (e) { /* silent — datalist just stays shorter */ }
    },
    async saveRecvDrug() {
      this.recvDrugError = '';
      if (!this.recvDrug.store_id) { this.recvDrugError = 'Select a receiving store.'; return; }
      if (!this.recvDrug.drug_name || !this.recvDrug.drug_name.trim()) { this.recvDrugError = 'Item name is required.'; return; }
      if (!(this.recvDrug.quantity > 0)) { this.recvDrugError = 'Quantity received must be greater than zero.'; return; }
      this.saving = true;
      try {
        const r = await executeJson({ function: 'receiveStoreDrugs', ...this.recvDrug, unit_cost: this.recvDrug.unit_cost || 0 }, hms);
        if (r.data && r.data.success) { this.recvDrugModal = false; await this.loadStoreStock(); }
        else this.recvDrugError = (r.data && r.data.message) || 'Failed.';
      } catch (e) { console.error(e); this.recvDrugError = 'Network error.'; }
      finally { this.saving = false; }
    },
    openXfer(d) {
      this.xferError = '';
      this.xfer = {
        store_stock_id: d.store_stock_id, drug_name: d.drug_name, batch_number: d.batch_number,
        store_name: d.store_name, available: parseFloat(d.quantity || 0), quantity: parseFloat(d.quantity || 0)
      };
      this.xferModal = true;
    },
    async saveXfer() {
      this.xferError = '';
      if (!(this.xfer.quantity > 0)) { this.xferError = 'Enter a quantity greater than zero.'; return; }
      if (this.xfer.quantity > this.xfer.available) { this.xferError = 'Only ' + this.xfer.available + ' available.'; return; }
      this.saving = true;
      try {
        const r = await executeJson({ function: 'transferStoreStockToPharmacy', store_stock_id: this.xfer.store_stock_id, quantity: this.xfer.quantity }, hms);
        if (r.data && r.data.success) { this.xferModal = false; await this.loadStoreStock(); }
        else this.xferError = (r.data && r.data.message) || 'Failed.';
      } catch (e) { console.error(e); this.xferError = 'Network error.'; }
      finally { this.saving = false; }
    },

    openItemModal(it) { this.item = it ? { ...this.blankItem(), ...it } : this.blankItem(); this.itemModal = true; },
    async saveItem() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveStoresItem', ...this.item }, hms);
        if (r.data && r.data.success) { this.itemModal = false; await this.loadItems(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openSupplierModal(s) { this.supplier = s ? { ...this.blankSupplier(), ...s } : this.blankSupplier(); this.supplierModal = true; },
    async toggleSupplier(s) {
      const activate = s.is_active != 1;
      if (!activate && !confirm('Deactivate supplier "' + s.name + '"? They will no longer appear in supplier dropdowns.')) return;
      try {
        const r = await executeJson({ function: 'toggleStoresSupplier', supplier_id: s.supplier_id, is_active: activate ? 1 : 0 }, hms);
        if (r.data && r.data.success) await this.loadAll();
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
    },
    async saveSupplier() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveStoresSupplier', ...this.supplier }, hms);
        if (r.data && r.data.success) { this.supplierModal = false; await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async loadPos() {
      try {
        const r = await executeJson({ function: 'getStoresPos', status: this.poStatusFilter }, hms);
        if (r.data && r.data.success) this.pos = (r.data.data && r.data.data.Pos) || [];
      } catch (e) { console.error(e); }
    },
    openPoModal() { this.po = this.blankPo(); this.poModal = true; },
    addPoLine() { this.po.lines.push({ item_id: null, quantity: null, unit_price: null }); },
    async savePo() {
      const items = (this.po.lines || []).filter(l => l.item_id && l.quantity > 0);
      if (!this.po.supplier_id || !items.length) { alert('Pick a supplier and at least one line.'); return; }
      this.saving = true;
      try {
        const r = await executeJson({ function: 'createStoresPo', supplier_id: this.po.supplier_id, order_date: this.po.order_date, expected_delivery: this.po.expected_delivery, revenue_source: this.po.revenue_source, notes: this.po.notes, items }, hms);
        if (r.data && r.data.success) { this.poModal = false; await this.loadPos(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async approvePo(p) {
      const r = await executeJson({ function: 'approveStoresPo', po_id: p.po_id }, hms);
      if (r.data && r.data.success) await this.loadPos();
    },
    async openPoDetail(p) {
      const r = await executeJson({ function: 'getStoresPoDetail', po_id: p.po_id }, hms);
      if (r.data && r.data.success) {
        const d = r.data.data || {};
        const lines = (d.Items || []).map(l => l.item_code + ' × ' + l.quantity + ' @ ' + l.unit_price).join('\n');
        alert(d.Po.po_number + ' — ' + d.Po.supplier_name + '\n\n' + lines);
      }
    },
    async openReceiveModal(p) {
      try {
        const r = await executeJson({ function: 'getStoresPoDetail', po_id: p.po_id }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.recv = {
            po_id: p.po_id, po_number: p.po_number, delivery_note: '',
            lines: (d.Items || []).map(l => ({
              po_item_id: l.po_item_id, item_code: l.item_code, item_name: l.item_name,
              quantity: parseFloat(l.quantity), received_qty: parseFloat(l.received_qty),
              now_receiving: parseFloat(l.quantity) - parseFloat(l.received_qty)
            }))
          };
          this.recvModal = true;
        }
      } catch (e) { console.error(e); }
    },
    async saveReceive() {
      const receipts = (this.recv.lines || []).filter(l => l.now_receiving > 0).map(l => ({ po_item_id: l.po_item_id, received_qty: l.now_receiving }));
      if (!receipts.length) { alert('Enter at least one received quantity.'); return; }
      this.saving = true;
      try {
        const r = await executeJson({ function: 'receiveStoresPo', po_id: this.recv.po_id, delivery_note: this.recv.delivery_note, receipts }, hms);
        if (r.data && r.data.success) { this.recvModal = false; await this.loadPos(); await this.loadItems(); alert('GRN ' + r.data.data.grn_number + ' recorded.'); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async loadReqs() {
      try {
        const r = await executeJson({ function: 'getStoresRequisitions' }, hms);
        if (r.data && r.data.success) this.reqs = (r.data.data && r.data.data.Requisitions) || [];
      } catch (e) { console.error(e); }
    },
    openReqModal() { this.req = { requesting_dept: '', lines: [{ item_id: null, quantity: null }] }; this.reqModal = true; },
    addReqLine() { this.req.lines.push({ item_id: null, quantity: null }); },
    async saveReq() {
      const items = (this.req.lines || []).filter(l => l.item_id && l.quantity > 0);
      if (!this.req.requesting_dept || !items.length) { alert('Department + items required.'); return; }
      this.saving = true;
      try {
        const r = await executeJson({ function: 'createStoresRequisition', requesting_dept: this.req.requesting_dept, items }, hms);
        if (r.data && r.data.success) { this.reqModal = false; await this.loadReqs(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async openIssueModal(r) {
      try {
        const res = await executeJson({ function: 'getStoresRequisitionDetail', req_id: r.req_id }, hms);
        if (res.data && res.data.success) {
          const d = res.data.data || {};
          this.issue = {
            req_id: r.req_id, req_number: r.req_number, requesting_dept: r.requesting_dept,
            lines: (d.Items || []).map(l => ({
              req_item_id: l.req_item_id, item_code: l.item_code, item_name: l.item_name, unit: l.unit,
              current_stock: parseFloat(l.current_stock || 0),
              quantity_requested: parseFloat(l.quantity_requested),
              quantity_issued: parseFloat(l.quantity_issued || 0),
              now_issuing: Math.min(parseFloat(l.quantity_requested) - parseFloat(l.quantity_issued || 0), parseFloat(l.current_stock || 0))
            }))
          };
          this.issueModal = true;
        }
      } catch (e) { console.error(e); }
    },
    async saveIssue() {
      const issues = (this.issue.lines || []).filter(l => l.now_issuing > 0).map(l => ({ req_item_id: l.req_item_id, quantity_issued: l.now_issuing }));
      if (!issues.length) { alert('Enter at least one quantity to issue.'); return; }
      this.saving = true;
      try {
        const r = await executeJson({ function: 'issueStoresRequisition', req_id: this.issue.req_id, issues }, hms);
        if (r.data && r.data.success) { this.issueModal = false; await this.loadReqs(); await this.loadItems(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    poStatusPill(s) {
      return ({ draft: 'hms-pill-muted', approved: 'hms-pill-info', sent: 'hms-pill-info',
                partially_received: 'hms-pill-warn', received: 'hms-pill-success', cancelled: 'hms-pill-danger' })[s] || '';
    },
    formatPoStatus(s) {
      return ({ draft: 'Draft', approved: 'Approved', sent: 'Sent', partially_received: 'Partial', received: 'Received', cancelled: 'Cancelled' })[s] || s;
    },
    reqStatusPill(s) {
      return ({ draft: 'hms-pill-muted', submitted: 'hms-pill-info', approved: 'hms-pill-info',
                partially_issued: 'hms-pill-warn', issued: 'hms-pill-success', rejected: 'hms-pill-danger', cancelled: 'hms-pill-danger' })[s] || '';
    },
    formatReqStatus(s) {
      return ({ draft: 'Draft', submitted: 'Submitted', approved: 'Approved', partially_issued: 'Partial', issued: 'Issued', rejected: 'Rejected', cancelled: 'Cancelled' })[s] || s;
    },

    formatNumber(n) { const x = parseFloat(n || 0); return x.toLocaleString(undefined, { maximumFractionDigits: 0 }); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 26px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }
.hms-section-label { font-size: 11px; font-weight: 700; color: #4a5568; letter-spacing: 1px; padding: 4px 0; border-bottom: 1px solid #edf2f7; margin: 8px 0 6px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-tag { padding: 2px 6px; border-radius: 6px; font-size: 10px; font-weight: 600; }
.hms-tag-warn { background: #feebc8; color: #7b341e; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }
.hms-form-group { margin-bottom: 12px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }
.text-danger { color: #c53030; }
.st-bad { color: #c53030; font-weight: 600; }

.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-form-error { background: #fed7d7; color: #742a2a; border-radius: 6px; padding: 6px 10px; font-size: 12px; }
.hms-info-banner { background: #ebf8ff; color: #2a4365; border: 1px solid #bee3f8; border-radius: 6px; padding: 8px 12px; font-size: 13px; }
</style>
